import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../../components/ui/dialog';
import { Tag, Plus, Edit, Trash2, CheckCircle2, X, Settings, ChevronDown, ChevronUp } from 'lucide-react';

export function CategoriasModal({
  open, onOpenChange, categoriasTarefas,
  editingCat, setEditingCat,
  novaCatNome, setNovaCatNome, novaCatCor, setNovaCatCor,
  onCriar, onAtualizar, onEliminar,
  operacoesDisponiveis
}) {
  const [configurandoCatId, setConfigurandoCatId] = useState(null);

  const handleToggleOp = async (catId, op, currentOps) => {
    const exists = currentOps.some(o => o.operacao_id === op.id);
    let newOps;
    if (exists) {
      newOps = currentOps.filter(o => o.operacao_id !== op.id);
    } else {
      newOps = [...currentOps, {
        operacao_id: op.id,
        operacao_codigo: op.codigo,
        operacao_nome: op.nome,
        tempo_standard: op.tempo_standard || 0
      }];
    }
    await onAtualizar(catId, { operacoes_sugeridas: newOps });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="categorias-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />Gerir Categorias
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {categoriasTarefas.map(cat => (
            <div key={cat.id} className="rounded-lg bg-stone-50 border" data-testid={`cat-item-${cat.id}`}>
              <div className="flex items-center gap-2 p-2">
                {editingCat?.id === cat.id ? (
                  <>
                    <input type="color" value={editingCat.cor}
                      onChange={(e) => setEditingCat({ ...editingCat, cor: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer border-0" />
                    <Input value={editingCat.nome}
                      onChange={(e) => setEditingCat({ ...editingCat, nome: e.target.value })} className="flex-1 h-8" />
                    <Button size="sm" className="h-8"
                      onClick={() => { onAtualizar(cat.id, { nome: editingCat.nome, cor: editingCat.cor }); setEditingCat(null); }}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingCat(null)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cat.cor }} />
                    <span className="flex-1 font-medium text-sm">{cat.nome}</span>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => setConfigurandoCatId(configurandoCatId === cat.id ? null : cat.id)}
                      title="Operacoes da categoria">
                      {configurandoCatId === cat.id ? <ChevronUp className="h-3.5 w-3.5" /> : <Settings className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => setEditingCat({ id: cat.id, nome: cat.nome, cor: cat.cor })}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                      onClick={() => onEliminar(cat.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
              {/* Operações associadas à categoria */}
              {configurandoCatId === cat.id && (
                <div className="px-2 pb-2 border-t border-stone-200 mt-1 pt-2">
                  <p className="text-[10px] font-bold uppercase text-stone-400 tracking-wider mb-1.5">Operacoes Sugeridas desta Categoria</p>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {(operacoesDisponiveis || []).map(op => {
                      const isSelected = (cat.operacoes_sugeridas || []).some(o => o.operacao_id === op.id);
                      return (
                        <button key={op.id} type="button"
                          onClick={() => handleToggleOp(cat.id, op, cat.operacoes_sugeridas || [])}
                          className={`px-2 py-1 rounded-md border text-xs font-medium transition-colors ${
                            isSelected ? 'bg-primary text-white border-primary' : 'bg-white text-stone-500 border-stone-200 hover:border-primary'
                          }`}>
                          <span className="font-mono mr-0.5">{op.codigo}</span> {op.nome}
                        </button>
                      );
                    })}
                  </div>
                  {(cat.operacoes_sugeridas || []).length > 0 && (
                    <p className="text-[10px] text-stone-400 mt-1">{(cat.operacoes_sugeridas || []).length} operacao(oes) associadas</p>
                  )}
                </div>
              )}
            </div>
          ))}
          {categoriasTarefas.length === 0 && (
            <div className="text-sm text-stone-400 text-center py-4">Sem categorias criadas</div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t">
            <input type="color" value={novaCatCor}
              onChange={(e) => setNovaCatCor(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border-0" data-testid="nova-cat-cor" />
            <Input placeholder="Nome da nova categoria..." value={novaCatNome}
              onChange={(e) => setNovaCatNome(e.target.value)} className="flex-1 h-8"
              onKeyDown={(e) => e.key === 'Enter' && onCriar()} data-testid="nova-cat-nome" />
            <Button size="sm" className="h-8" onClick={onCriar} disabled={!novaCatNome.trim()} data-testid="nova-cat-criar-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />Criar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
